import { useRef, useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';

import {
  CANVAS,
  TREE,
  FIREPLACE,
  GIFTS,
  SOCKS,
  DECORATIONS,
} from '../config/positions';

import { GiftBox } from './GiftBox';
import { DecorationItem } from './DecorationItem';
import { FireBurst } from './FireBurst';

import { useHolidayStore } from '@/store/useHolidayStore';

const ASSET_PATH = '/christmas-tree-campaign';

export function HolidayScene() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const { fireplaceOn, toggleFireplace, gifts } = useHolidayStore();

  // Calculate scale factor to fit container while maintaining aspect ratio
  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) return;

      const container = containerRef.current.parentElement;
      if (!container) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Calculate scale to fit while maintaining aspect ratio
      const scaleX = containerWidth / CANVAS.width;
      const scaleY = containerHeight / CANVAS.height;
      const newScale = Math.min(scaleX, scaleY);

      setScale(newScale);
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  // Scaled dimensions
  const scaledWidth = CANVAS.width * scale;
  const scaledHeight = CANVAS.height * scale;

  return (
    <div className="w-full h-full flex items-center justify-center overflow-hidden bg-black">
      {/* Fixed aspect ratio container */}
      <div
        ref={containerRef}
        className="relative"
        style={{
          width: `${scaledWidth}px`,
          height: `${scaledHeight}px`,
        }}
      >
        {/* Layer 1: Background - fills entire container */}
        <img
          src={`${ASSET_PATH}/0_XMAS_tree_Background 1.svg`}
          alt="Holiday Background"
          className="absolute inset-0 w-full h-full"
          style={{ zIndex: 0 }}
          draggable={false}
        />

        {/* Room ambient lighting - warm glow that pulses with the fire */}
        <div
          className={`absolute inset-0 pointer-events-none transition-opacity duration-1000 ${
            fireplaceOn ? 'animate-room-light' : 'opacity-0'
          }`}
          style={{
            zIndex: 1,
            background: `radial-gradient(
              ellipse 80% 60% at ${((FIREPLACE.x + FIREPLACE.width / 2) / CANVAS.width) * 100}% ${((FIREPLACE.y + FIREPLACE.height / 2) / CANVAS.height) * 100}%,
              rgba(255, 120, 40, 0.25) 0%,
              rgba(255, 80, 20, 0.15) 30%,
              rgba(255, 50, 0, 0.05) 60%,
              transparent 100%
            )`,
          }}
        />

        {/* Layer 2: Tree */}
        <img
          src={`${ASSET_PATH}/Tree.svg`}
          alt="Holiday Tree"
          className="absolute pointer-events-none"
          style={{
            zIndex: TREE.zIndex,
            left: `${(TREE.x / CANVAS.width) * 100}%`,
            top: `${(TREE.y / CANVAS.height) * 100}%`,
            width: `${(TREE.width / CANVAS.width) * 100}%`,
            height: `${(TREE.height / CANVAS.height) * 100}%`,
          }}
          draggable={false}
        />

        {/* Layer 3: Gifts (draggable) */}
        {gifts.map((gift) => {
          // Find the config for this gift
          const config = GIFTS.find((g) => g.id === gift.id);
          if (!config) return null;

          return (
            <GiftBox
              key={gift.id}
              gift={gift}
              config={config}
              canvasSize={CANVAS}
              fireplaceConfig={FIREPLACE}
            />
          );
        })}

        {/* Layer 4: Fireplace click zone (invisible, for lighting fire) */}
        {!fireplaceOn && (
          <div
            className="absolute cursor-pointer hover:bg-white/10 transition-colors rounded-lg"
            style={{
              zIndex: FIREPLACE.zIndex - 1,
              left: `${(FIREPLACE.x / CANVAS.width) * 100}%`,
              top: `${(FIREPLACE.y / CANVAS.height) * 100}%`,
              width: `${(FIREPLACE.width / CANVAS.width) * 100}%`,
              height: `${(FIREPLACE.height / CANVAS.height) * 100}%`,
            }}
            onClick={toggleFireplace}
            title="Click to light the fire"
          />
        )}

        {/* Layer 5: Fireplace ON (flames) - shown when lit */}
        <img
          src={`${ASSET_PATH}/FirePlaceON.svg`}
          alt="Fireplace with flames"
          className={`absolute pointer-events-none transition-opacity duration-500 ${
            fireplaceOn ? 'opacity-100 animate-fire-flicker' : 'opacity-0'
          }`}
          style={{
            zIndex: FIREPLACE.zIndex,
            left: `${(FIREPLACE.x / CANVAS.width) * 100}%`,
            top: `${(FIREPLACE.y / CANVAS.height) * 100}%`,
            width: `${(FIREPLACE.width / CANVAS.width) * 100}%`,
            height: `${(FIREPLACE.height / CANVAS.height) * 100}%`,
          }}
          draggable={false}
          id="fireplace-zone"
        />

        {/* Fire glow effect when lit */}
        {fireplaceOn && (
          <div
            className="absolute rounded-full animate-fire-glow pointer-events-none"
            style={{
              zIndex: FIREPLACE.zIndex - 1,
              left: `${((FIREPLACE.x + FIREPLACE.width / 2) / CANVAS.width) * 100}%`,
              top: `${((FIREPLACE.y + FIREPLACE.height / 2) / CANVAS.height) * 100}%`,
              width: `${(150 / CANVAS.width) * 100}%`,
              height: `${(120 / CANVAS.height) * 100}%`,
              transform: 'translate(-50%, -50%)',
            }}
          />
        )}

        {/* Fire burst effect when gift burns */}
        <FireBurst />

        {/* Layer 6: Socks */}
        {SOCKS.map((sock, index) => (
          <img
            key={sock.id}
            src={`${ASSET_PATH}/${sock.id}.svg`}
            alt={sock.id}
            className="absolute pointer-events-none animate-sock-glow"
            style={{
              zIndex: sock.zIndex,
              left: `${(sock.x / CANVAS.width) * 100}%`,
              top: `${(sock.y / CANVAS.height) * 100}%`,
              width: `${(sock.width / CANVAS.width) * 100}%`,
              height: `${(sock.height / CANVAS.height) * 100}%`,
              animationDelay: `${index * 0.5}s`,
            }}
            draggable={false}
          />
        ))}

        {/* Layer 7: Decorations (clickable for fun) */}
        {DECORATIONS.map((decoration, index) => (
          <DecorationItem
            key={decoration.id}
            decoration={decoration}
            canvasSize={CANVAS}
            animationDelay={index * 0.7}
          />
        ))}

        {/* Ambient tree glow */}
        <div
          className="absolute pointer-events-none animate-tree-glow"
          style={{
            zIndex: TREE.zIndex - 1,
            left: `${((TREE.x + TREE.width / 2) / CANVAS.width) * 100}%`,
            top: `${((TREE.y + TREE.height * 0.4) / CANVAS.height) * 100}%`,
            width: `${(TREE.width * 0.8 / CANVAS.width) * 100}%`,
            height: `${(TREE.height * 0.6 / CANVAS.height) * 100}%`,
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(ellipse, rgba(0, 255, 100, 0.15) 0%, rgba(255, 215, 0, 0.1) 40%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(30px)',
          }}
        />

        {/* Bouncing arrow pointing at fireplace when fire is off */}
        {!fireplaceOn && (
          <div
            className="absolute pointer-events-none animate-bounce"
            style={{
              zIndex: 100,
              left: `${((FIREPLACE.x + FIREPLACE.width / 2) / CANVAS.width) * 100}%`,
              top: `${((FIREPLACE.y - 40) / CANVAS.height) * 100}%`,
              transform: 'translateX(-50%)',
            }}
          >
            <ChevronDown
              className="w-12 h-12"
              style={{
                color: '#FFD700',
                filter: 'drop-shadow(0 0 10px rgba(255, 150, 0, 0.8)) drop-shadow(0 0 20px rgba(255, 100, 0, 0.6))',
              }}
              strokeWidth={3}
            />
          </div>
        )}
      </div>
    </div>
  );
}
